import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/I18nContext";
import { toast } from "sonner";
import { apiError } from "@/lib/api";

export default function Auth() {
  const { login, register } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", sex: "", dob: "" });
  const [busy, setBusy] = useState(false);

  const postLoginTarget = (u) => {
    if (next && next.startsWith("/")) return next;
    return u.is_admin ? "/admin" : "/account";
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = mode === "login"
        ? await login(form.email, form.password)
        : await register(form);
      const namePart = u.name ? `, ${u.name.split(" ")[0]}` : "";
      toast.success(t("auth.welcome_toast", { name: namePart }));
      nav(postLoginTarget(u));
    } catch (err) {
      toast.error(apiError(err, t("auth.failed")));
    } finally { setBusy(false); }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (next && next.startsWith("/")) {
      sessionStorage.setItem("brunchi_auth_next", next);
    }
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const input = "w-full px-4 py-3 rounded-lg border border-[color:var(--brunchi-stone-2)] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--brunchi-terracotta)]";

  return (
    <div className="max-w-md mx-auto px-6 py-16" data-testid="auth-page">
      <h1 className="font-serif text-3xl font-bold mb-2">
        {mode === "login" ? t("auth.welcome_back") : t("auth.create_account")}
      </h1>
      <p className="text-sm text-[color:var(--brunchi-ink-soft)] mb-8">{t("auth.sub")}</p>

      <button onClick={googleSignIn} data-testid="google-signin-btn"
        className="w-full flex items-center justify-center gap-3 py-3 rounded-full border border-[color:var(--brunchi-stone-2)] bg-white hover:bg-[color:var(--brunchi-cream-2)] font-medium text-sm mb-4">
        <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
          <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
          <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
          <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.3l-6.2-5.2C29.1 34.9 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
          <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.2 5.2C41 34.9 44 30 44 24c0-1.3-.1-2.4-.4-3.5z"/>
        </svg>
        {t("auth.google")}
      </button>

      <div className="flex items-center gap-3 my-4 text-xs uppercase tracking-widest text-[color:var(--brunchi-ink-soft)]">
        <div className="flex-1 h-px bg-[color:var(--brunchi-stone)]" />
        <span>{t("auth.or")}</span>
        <div className="flex-1 h-px bg-[color:var(--brunchi-stone)]" />
      </div>

      <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
        {mode === "register" && (
          <input required placeholder={t("auth.name")} value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} data-testid="auth-name" />
        )}
        <input required type="email" placeholder={t("auth.email")} value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} data-testid="auth-email" />
        <input required type="password" placeholder={t("auth.password")} value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })} className={input} data-testid="auth-password" />
        {mode === "register" && (
          <>
            <select value={form.sex} onChange={(e) => setForm({ ...form, sex: e.target.value })} className={input} data-testid="auth-sex">
              <option value="">{t("auth.sex_optional")}</option>
              <option value="female">{t("auth.sex_female")}</option>
              <option value="male">{t("auth.sex_male")}</option>
              <option value="other">{t("auth.sex_other")}</option>
            </select>
            <input type="date" placeholder={t("auth.dob_optional")} value={form.dob}
              onChange={(e) => setForm({ ...form, dob: e.target.value })} className={input} data-testid="auth-dob" />
          </>
        )}
        <button disabled={busy} type="submit"
          className="w-full py-3 rounded-full pill-primary font-medium disabled:opacity-50"
          data-testid="auth-submit-btn">
          {busy ? t("auth.busy") : mode === "login" ? t("auth.sign_in") : t("auth.create_btn")}
        </button>
      </form>

      <button onClick={() => setMode(mode === "login" ? "register" : "login")}
        data-testid="auth-mode-toggle"
        className="mt-6 text-sm text-[color:var(--brunchi-terracotta)] hover:underline">
        {mode === "login" ? t("auth.new_here") : t("auth.already")}
      </button>
    </div>
  );
}
